import React, { Fragment, useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import TreatmentProtocolForm from '../../components/TreatmentProtocolForm';
// endpoints
import { createPigGroupTreatmentProtocol } from 'endpoints/treatmentProtocols';
// utils
import { connect } from 'react-redux';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatTreatmentProtocolPayload } from '../../helper';
import moment from 'moment';

const initialTreatment = { product: '', steps: [{}], };
const defaultInitialValues = { treatments: [initialTreatment] };

const CreateGroupTreatmentProtocol = ({
  pigGroup,
  setBreadcrumbs,
  setPageOptions,
  openModal,
  router: { params: { farmId, groupId }, push },
  location,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { returnUrl } = location.query || {};
  useEffect(() => {
    const backLink = returnUrl || `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`;
    setPageOptions({ backLink });
  }, [returnUrl, farmId, groupId]);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: pigGroup?.farm?.name || '',
        path: `/treatment-protocols-farms/${farmId}/groups`,
      },
      {
        label: pigGroup?.name || '',
        path: `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`,
      },
      {
        label: <FormattedMessage id="general.createTreatmentProtocol" />,
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [pigGroup?.name]);

  const hasOverdueSteps = (protocolData) => {
    const currentDate = moment(pigGroup?.farm?.current_date).startOf('day');
    const groupStartedOn = pigGroup?.started_on ? moment(pigGroup.started_on).startOf('day') : currentDate;
    const baseDate = protocolData?.tp_base_date ? currentDate : groupStartedOn;

    const isDateBeforeToday = (date) => date && moment(date).isBefore(currentDate, 'day');

    const getEndDate = (step) => {
      const { step_type } = step || {};
      const data = step?.[step_type] || {};
      const { step_unit, first_period, last_period, start_date, end_date } = data;
      if (step_unit === 'date_range') {
        return end_date || start_date;
      }
      if (!step_unit || !first_period) return null;

      if (step_type === 'before') {
        return moment(baseDate).add(first_period, step_unit);
      }
      if (step_type === 'between') {
        if (!last_period) return null;
        return moment(baseDate).add((last_period - 1), step_unit);
      }
      if (step_type === 'on') {
        if (step_unit === 'week') {
          return moment(baseDate).add(first_period, step_unit);
        }
        return moment(baseDate).add((first_period - 1), step_unit);
      }
      // 'after' is ongoing; not considered overdue
      return null;
    };

    try {
      return (protocolData?.treatments || []).some((treatment) => (treatment?.steps || []).some((step) => {
        const end = getEndDate(step);
        if (end) return isDateBeforeToday(end);
        // handle single-day 'on' without end_date (unlikely)
        const { step_type } = step || {};
        const data = step?.[step_type] || {};
        const { start_date } = data || {};
        return step_type === 'on' && start_date && isDateBeforeToday(start_date);
      }));
    } catch (e) {
      return false;
    }
  };

  const performCreate = async (newProtocolData) => {
    try {
      setIsLoading(true);
      const treatmentProtocolPayload = formatTreatmentProtocolPayload(newProtocolData);
      await createPigGroupTreatmentProtocol(groupId, treatmentProtocolPayload);
      push({
        pathname: returnUrl || `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`,
        state: { shouldRefresh: true }
      });
    } catch (error) {
      toastResponseErrors(error);
      setIsLoading(false);
    }
  };

  const onSubmit = (newProtocolData) => {
    if (hasOverdueSteps(newProtocolData)) {
      openModal(
        <ConfirmationModal
          noBorder
          warningMessage={<FormattedMessage id="component.treatmentProtocols.confirmOverdueSteps.warning" />}
          handleConfirm={() => performCreate(newProtocolData)}
          actionBtnLabel={<FormattedMessage id="general.createTreatmentProtocol" />}
          actionBtnProps={{ primary: true }}
          title={<FormattedMessage id="component.treatmentProtocols.confirmOverdueSteps.title" />}
        />
      );
      return;
    }
    performCreate(newProtocolData);
  };

  return (
    <Fragment>
      <Preloader isActive={isLoading} />
      <TreatmentProtocolForm
        isCreate
        onSubmit={onSubmit}
        initialValues={defaultInitialValues}
        title={<FormattedMessage id="general.createTreatmentProtocol" />}
        submitLabel={<FormattedMessage id="general.createTreatmentProtocol" />}
        ABFEnabled={pigGroup.abf_tracking}
        createScopeNotice={(
          <FormattedMessage
            id="component.treatmentProtocols.createGroupScopeWarning"
            values={{
              w: (chunks) => <b>{chunks}</b>,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        )}
      />
    </Fragment>
  );
};

CreateGroupTreatmentProtocol.propTypes = {
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  openModal: T.func.isRequired,
  pigGroup: T.object.isRequired,
  router: T.object.isRequired,
  location: T.object.isRequired,
};

export default connect(
  (state) => ({
    pigGroup: state.profiles.pigGroup.data,
  }), {
    setPageOptions,
    setBreadcrumbs,
    openModal,
  }
)(CreateGroupTreatmentProtocol);
